import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  SearchBox,
  Spinner,
  Toggle,
  cn,
  formatBusinessHours,
  useAuth,
  useToast,
} from "@toss-ads/ui";
import {
  createAdvertiser,
  deleteAdvertiser,
  fetchAdvertiserSummariesAdmin,
  listAdvertisers,
  updateAdvertiser,
} from "../lib/api";
import type { Advertiser } from "../lib/types";

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const apiBase =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "https://ad3.skyventures.co.kr";

// admin 용 광고주 목록 — 평면 리스트. 플랫폼은 광고주 속성이 아니라 lead 속성이라
// 여기선 안 묶고, 광고주 상세에서 플랫폼별로 분기해서 본다.
export function AdvertisersListPage() {
  const [search, setSearch] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Advertiser | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
  });

  const filtered = React.useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((a) => a.name.toLowerCase().includes(q));
  }, [data, search]);

  const { data: summaries } = useQuery({
    queryKey: ["admin", "advertisers", "summaries"],
    queryFn: fetchAdvertiserSummariesAdmin,
  });
  const summaryById = React.useMemo(
    () => new Map((summaries ?? []).map((s) => [s.advertiserId, s])),
    [summaries],
  );

  const { user } = useAuth();
  const isDeveloper = user?.role === "developer";
  // 컬럼: 닉네임 · [slug(dev)] · 전체 · 테스트 · 상태 · 생성일 · 수정.
  const colSpan = isDeveloper ? 8 : 7;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-[18px] font-semibold tracking-tight">광고주</h1>
        {isDeveloper && (
          <Button size="md" onClick={() => setCreateOpen(true)}>
            + 새 광고주
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <SearchBox value={search} onChange={setSearch} placeholder="닉네임 검색" />
      </div>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-surface-muted">
            <tr>
              <Th>닉네임</Th>
              {isDeveloper && <Th width={150}>slug</Th>}
              <Th width={80}>전체</Th>
              <Th width={80}>테스트</Th>
              <Th width={80}>삭제됨</Th>
              <Th width={90}>상태</Th>
              <Th width={130}>영업시간</Th>
              <Th width={110}>생성일</Th>
              <Th width={72}>수정</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={colSpan} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={colSpan} className="py-12 text-center">
                  <p className="text-[13px] text-danger mb-2">
                    {error instanceof ApiError
                      ? error.message
                      : "데이터를 불러오지 못했습니다."}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetch()}
                  >
                    다시 시도
                  </Button>
                </td>
              </tr>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <tr>
                <td
                  colSpan={colSpan}
                  className="py-12 text-center text-[13px] text-ink-subtle"
                >
                  {data && data.length > 0
                    ? "검색 조건과 일치하는 광고주가 없습니다."
                    : "등록된 광고주가 없습니다."}
                </td>
              </tr>
            )}
            {filtered.map((a) => {
              const to = `/advertisers/${a.id}`;
              const s = summaryById.get(a.id);
              return (
                <tr
                  key={a.id}
                  className="border-t border-border hover:bg-surface-muted transition-colors"
                >
                  <LinkTd to={to} className="font-medium text-ink">
                    {a.name}
                  </LinkTd>
                  {isDeveloper && (
                    <LinkTd
                      to={to}
                      className="font-mono text-[12px] text-ink-muted"
                    >
                      {a.slug}
                    </LinkTd>
                  )}
                  <LinkTd to={to} className="tabular-nums text-ink font-medium">
                    {s ? s.total.toLocaleString() : "—"}
                  </LinkTd>
                  <LinkTd
                    to={`${to}?tab=leads&test=test`}
                    className={cn(
                      "tabular-nums",
                      s && s.testTotal > 0
                        ? "text-warning font-medium underline decoration-dotted"
                        : "text-ink-subtle",
                    )}
                  >
                    {s ? s.testTotal.toLocaleString() : "—"}
                  </LinkTd>
                  <LinkTd
                    to={`${to}?tab=leads&test=deleted`}
                    className={cn(
                      "tabular-nums",
                      s && s.deletedTotal > 0
                        ? "text-danger font-medium underline decoration-dotted"
                        : "text-ink-subtle",
                    )}
                  >
                    {s ? s.deletedTotal.toLocaleString() : "—"}
                  </LinkTd>
                  <LinkTd to={to}>
                    <Badge variant={a.active ? "success" : "neutral"} dot>
                      {a.active ? "활성" : "비활성"}
                    </Badge>
                  </LinkTd>
                  <LinkTd to={to} className="text-ink-muted text-[11px] leading-tight">
                    <div>평일 {formatBusinessHours(a.weekdayOpen, a.weekdayClose)}</div>
                    <div>주말 {formatBusinessHours(a.weekendOpen, a.weekendClose)}</div>
                  </LinkTd>
                  <LinkTd to={to} className="text-ink-muted">
                    {formatDate(a.createdAt)}
                  </LinkTd>
                  <td className="px-2.5 py-3 align-middle">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(a)}
                    >
                      수정
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateAdvertiserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <EditAdvertiserModal
        advertiser={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

// 광고주 수정 — 이름(전 스태프) + developer 는 slug·활성·webhook secret 까지.
function EditAdvertiserModal({
  advertiser,
  onClose,
}: {
  advertiser: Advertiser | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const isDeveloper = user?.role === "developer";

  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [secret, setSecret] = React.useState("");
  const [slugErr, setSlugErr] = React.useState<string | null>(null);
  const [confirmDel, setConfirmDel] = React.useState(false);
  const [wdOpen, setWdOpen] = React.useState("");
  const [wdClose, setWdClose] = React.useState("");
  const [weOpen, setWeOpen] = React.useState("");
  const [weClose, setWeClose] = React.useState("");

  React.useEffect(() => {
    if (advertiser) {
      setName(advertiser.name);
      setSlug(advertiser.slug);
      setActive(advertiser.active);
      setSecret("");
      setSlugErr(null);
      setConfirmDel(false);
      setWdOpen(advertiser.weekdayOpen ?? "");
      setWdClose(advertiser.weekdayClose ?? "");
      setWeOpen(advertiser.weekendOpen ?? "");
      setWeClose(advertiser.weekendClose ?? "");
    }
  }, [advertiser]);

  const del = useMutation({
    mutationFn: () => deleteAdvertiser(advertiser!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "advertisers"] });
      toast.success("삭제되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!advertiser) throw new Error("no advertiser");
      const patch: {
        name?: string;
        slug?: string;
        active?: boolean;
        webhookSecret?: string;
        weekdayOpen?: string;
        weekdayClose?: string;
        weekendOpen?: string;
        weekendClose?: string;
      } = {};
      if (name.trim() !== advertiser.name) patch.name = name.trim();
      // 영업시간 — 운영자(admin) 편집. 빈값이면 해제로 전송.
      if (wdOpen !== (advertiser.weekdayOpen ?? "")) patch.weekdayOpen = wdOpen;
      if (wdClose !== (advertiser.weekdayClose ?? "")) patch.weekdayClose = wdClose;
      if (weOpen !== (advertiser.weekendOpen ?? "")) patch.weekendOpen = weOpen;
      if (weClose !== (advertiser.weekendClose ?? "")) patch.weekendClose = weClose;
      if (isDeveloper) {
        if (slug.trim() !== advertiser.slug) patch.slug = slug.trim();
        if (active !== advertiser.active) patch.active = active;
        if (secret.trim().length > 0) patch.webhookSecret = secret.trim();
      }
      return updateAdvertiser(advertiser.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "advertisers"] });
      if (advertiser)
        qc.invalidateQueries({
          queryKey: ["admin", "advertisers", advertiser.id],
        });
      toast.success("저장되었습니다");
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setSlugErr("이미 사용 중인 slug 입니다");
      } else {
        toast.error(e instanceof ApiError ? e.message : "저장 실패");
      }
    },
  });

  const slugOk = !isDeveloper || SLUG_RE.test(slug.trim());
  const secretOk =
    !isDeveloper || secret.trim().length === 0 || secret.trim().length >= 8;
  const valid = name.trim().length > 0 && slugOk && secretOk;

  return (
    <Modal
      open={!!advertiser}
      onClose={onClose}
      title="광고주 수정"
      description={
        isDeveloper
          ? "이름·slug·활성·webhook secret 을 수정합니다."
          : "광고주 이름을 수정합니다."
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="edit-adv-name">닉네임</Label>
          <Input
            id="edit-adv-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div>
          <Label>영업시간 (KST)</Label>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="w-8 text-[12px] text-ink-muted">평일</span>
              <Input
                type="time"
                value={wdOpen}
                onChange={(e) => setWdOpen(e.target.value)}
                className="w-[120px]"
              />
              <span className="text-ink-subtle">~</span>
              <Input
                type="time"
                value={wdClose}
                onChange={(e) => setWdClose(e.target.value)}
                className="w-[120px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-8 text-[12px] text-ink-muted">주말</span>
              <Input
                type="time"
                value={weOpen}
                onChange={(e) => setWeOpen(e.target.value)}
                className="w-[120px]"
              />
              <span className="text-ink-subtle">~</span>
              <Input
                type="time"
                value={weClose}
                onChange={(e) => setWeClose(e.target.value)}
                className="w-[120px]"
              />
            </div>
          </div>
          <p className="text-[11px] text-ink-subtle mt-1">
            비우면 미설정. 컨택시간 통계는 영업시간 내 인입 리드만 집계합니다.
          </p>
        </div>

        {isDeveloper && (
          <>
            <div>
              <Label htmlFor="edit-adv-slug">slug</Label>
              <Input
                id="edit-adv-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugErr(null);
                }}
                autoComplete="off"
                className="font-mono"
                error={!!slugErr}
              />
              {slugErr ? (
                <p className="text-[11px] text-danger mt-1">{slugErr}</p>
              ) : (
                <p className="text-[11px] text-warning mt-1">
                  ⚠️ slug 변경 시 벤더/토스의 webhook URL 도 함께 바꿔야 합니다.
                </p>
              )}
            </div>

            <div>
              <Label>webhook URL</Label>
              <code className="block text-[11px] font-mono text-ink-muted break-all">
                {apiBase}/api/webhooks/toss-ads/
                <span className="text-primary font-semibold">
                  {slug || "<suffix>"}
                </span>
                /leads
              </code>
            </div>

            <div>
              <Label htmlFor="edit-adv-secret">webhook secret</Label>
              <Input
                id="edit-adv-secret"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="off"
                className="font-mono text-[11px]"
                placeholder={`현재: ${advertiser?.webhookSecretPreview ?? ""}•••••• (변경 시 입력)`}
              />
              <p className="text-[11px] text-ink-subtle mt-1">
                공란 시 기존 secret 유지. 값 입력 시 교체 (최소 8자).
              </p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div>
                <div className="text-[13px] text-ink">활성</div>
                <div className="text-[11px] text-ink-subtle">
                  끄면 이 광고주의 webhook 수신이 막힙니다.
                </div>
              </div>
              <Toggle checked={active} onCheckedChange={setActive} />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-4 mt-4 border-t border-border">
        {isDeveloper && confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-danger">삭제?</span>
            <Button
              size="sm"
              variant="danger"
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              삭제 확정
            </Button>
            <Button size="sm" variant="text" onClick={() => setConfirmDel(false)}>
              취소
            </Button>
          </div>
        ) : isDeveloper ? (
          <Button
            size="sm"
            variant="text"
            className="text-danger"
            onClick={() => setConfirmDel(true)}
          >
            삭제
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="text" size="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateAdvertiserModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugErr, setSlugErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugErr(null);
    }
  }, [open]);

  const create = useMutation({
    // 토스 secret 없이 생성 — 서버가 랜덤 발급(외부전용 광고주). 생성 후 목록 유지.
    mutationFn: () => createAdvertiser({ name: name.trim(), slug: slug.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "advertisers"] });
      toast.success("광고주가 등록되었습니다");
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setSlugErr("이미 사용 중인 slug 입니다");
      } else {
        toast.error(e instanceof ApiError ? e.message : "등록 실패");
      }
    },
  });

  const valid = name.trim().length > 0 && SLUG_RE.test(slug.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 광고주"
      description="리드 수집 버킷(법무법인 단위)을 만듭니다. 토스 secret 은 외부전용이면 비워도 됩니다."
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="new-adv-name">닉네임</Label>
          <Input
            id="new-adv-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="법무법인 세담(이혼)"
            autoComplete="off"
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            어드민·목록에 보이는 이름.
          </p>
        </div>
        <div>
          <Label htmlFor="new-adv-slug">slug</Label>
          <Input
            id="new-adv-slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugErr(null);
            }}
            placeholder="sedam-divorce"
            autoComplete="off"
            className="font-mono"
            error={!!slugErr}
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            webhook URL 에 들어감 — 영문 소문자·숫자·하이픈. 한 번 정하면 가급적
            변경 X.
          </p>
          {slugErr && (
            <p className="text-[11px] text-danger mt-1">{slugErr}</p>
          )}
        </div>
        <p className="text-[11px] text-ink-subtle">
          토스 webhook 을 쓰는 광고주면, 등록 후 목록의 <b>수정</b> 에서 webhook
          secret 을 입력하세요. (developer)
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!valid}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          등록
        </Button>
      </div>
    </Modal>
  );
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function LinkTd({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className="p-0 align-middle">
      <Link
        to={to}
        className={cn(
          "block px-2.5 py-3.5 text-inherit no-underline",
          className,
        )}
      >
        {children}
      </Link>
    </td>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}
